import axios from 'axios';
import type { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { config } from './config';
import { logNetworkError } from '../utils/errorLogger';
import { getUserFriendlyErrorMessage, shouldRedirectToLogin } from '../utils/errorMessages';
import { retryWithBackoff, DEFAULT_RETRY_CONFIG } from '../utils/networkUtils';
import { clearAuthAndRedirect } from '../utils/authUtils';

// Toast notification callback (will be set by the app)
let toastCallback: ((type: 'error' | 'success', message: string) => void) | null = null;

export function setToastCallback(callback: (type: 'error' | 'success', message: string) => void) {
  toastCallback = callback;
}

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: `${config.api.baseUrl}/api/${config.api.version}`,
  timeout: config.api.timeout,
});

// Request interceptor to add authentication headers
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    if (config.data instanceof FormData && config.headers) {
      delete config.headers['Content-Type'];
    }

    // Try JWT token first (for auth endpoints)
    const authToken = localStorage.getItem('auth_token');
    if (authToken && config.headers) {
      config.headers.Authorization = `Bearer ${authToken}`;
    }

    // Add API key if available (for payment endpoints)
    const apiKey = localStorage.getItem('apiKey');
    if (apiKey && config.headers) {
      config.headers['X-API-Key'] = apiKey;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error: AxiosError) => {
    // Log the network error
    const url = error.config?.url || 'unknown';
    logNetworkError(error, url);

    // Get user-friendly error message
    const errorMessage = getUserFriendlyErrorMessage(error);

    // Handle authentication errors
    if (shouldRedirectToLogin(error)) {
      // Clear stored credentials and redirect to login
      clearAuthAndRedirect();
    }

    // Show toast notification if callback is set, but not for login errors
    // (login page handles its own error display)
    const requestUrl = error.config?.url || '';
    const isLoginError = requestUrl.includes('/auth/login') || requestUrl.includes('/login');

    if (toastCallback && !isLoginError) {
      toastCallback('error', errorMessage);
    }

    return Promise.reject(error);
  }
);

// Enhanced API client with retry functionality
export const apiClientWithRetry = {
  get: <T>(url: string, config?: any) =>
    retryWithBackoff(() => apiClient.get<T>(url, config), DEFAULT_RETRY_CONFIG, `GET ${url}`),

  post: <T>(url: string, data?: any, config?: any) =>
    retryWithBackoff(
      () => apiClient.post<T>(url, data, config),
      DEFAULT_RETRY_CONFIG,
      `POST ${url}`
    ),

  put: <T>(url: string, data?: any, config?: any) =>
    retryWithBackoff(() => apiClient.put<T>(url, data, config), DEFAULT_RETRY_CONFIG, `PUT ${url}`),

  delete: <T>(url: string, config?: any) =>
    retryWithBackoff(() => apiClient.delete<T>(url, config), DEFAULT_RETRY_CONFIG, `DELETE ${url}`),

  patch: <T>(url: string, data?: any, config?: any) =>
    retryWithBackoff(
      () => apiClient.patch<T>(url, data, config),
      DEFAULT_RETRY_CONFIG,
      `PATCH ${url}`
    ),
};

// Standard API client methods
export const apiClientMethods = {
  get: <T>(url: string, params?: any): Promise<T> =>
    apiClient.get<T>(url, { params }).then((response) => response.data),

  post: <T>(url: string, data?: any, config?: any): Promise<T> =>
    apiClient.post<T>(url, data, config).then((response) => response.data),

  put: <T>(url: string, data?: any, config?: any): Promise<T> =>
    apiClient.put<T>(url, data, config).then((response) => response.data),

  delete: <T>(url: string): Promise<T> =>
    apiClient.delete<T>(url).then((response) => response.data),

  patch: <T>(url: string, data?: any, config?: any): Promise<T> =>
    apiClient.patch<T>(url, data, config).then((response) => response.data),
};

export default apiClient;
export { apiClient };
